import PropTypes from "prop-types";
import React from "react";
import Language from "../lib/Language";
import Log from "../lib/Log";
import FieldError from "./FieldError";
import _ from "underscore";
import { TimeConstants as TC } from "../lib/TimeConstants";
import { MillenniumTime } from "@timeedit/millennium-time";
import { prependProtocolToUrl } from "../lib/UrlTools";
const { TextArea } = Input;
import { TFieldValue } from "../types/components/SplitObjectDialog";
import { TFieldDefClass } from "../types/api/APIClassTypes";
import API from "../lib/TimeEditAPI";
import { TimeEdit } from "../lib/TimeEdit";
import { FieldKind } from "../lib/FieldKind";
import { Checkbox, Input, InputRef, Select } from "@timeedit/ui-components/antd";

export type { FieldKindType } from "../lib/FieldKind";

const ROWS_COMPACT = 2;
const ROWS_LARGE = 5;

const UNIQUE_CHECK_DEBOUNCE_MS = 1500;

/**
 * Which of _validateInput's rules to apply. A rule left out is applied; only an
 * explicit false switches one off, so that a value on its way to becoming valid
 * is not rejected while it is being typed.
 */
type TValidationRules = {
    mandatory?: boolean;
    length?: boolean;
    integer?: boolean;
    range?: boolean;
    filter?: boolean;
};

/** The class names _renderInput builds up for the input it renders. */
type TFieldClassMap = {
    field?: boolean;
    validField?: boolean;
    invalidField?: boolean;
    nonCategoryInput?: boolean;
    lengthInput?: boolean;
};

type TFieldInputProps = {
    field: TFieldValue;
    definition: TFieldDefClass;
    /** MultiObjectSelect passes neither this nor isFirstField. */
    indeterminate?: boolean;
    editable: boolean;
    value: any; // TODO: Need to verify which types values can have. Seem to be at least string and number.
    isFirstField?: boolean;
    isCompact: boolean;
    useTEUI: boolean;
    /** Answers the changed value and the event that carried it. */
    onChange?: (value: string, event) => void;
    /** Only a multiple-value field gets one, and only then a remove button. */
    onDelete?: () => void;
    hideValidationError?: boolean;
    checkUniqueObjectField?: boolean;
    objectId?: number;
    onUniqueErrorChange?: (error?: string) => void;
};

type TFieldInputState = {
    validationError?: string;
    uniqueError?: string;
};

class FieldInput extends React.Component<TFieldInputProps, TFieldInputState> {
    static propTypes = {
        value: PropTypes.string.isRequired,
        field: PropTypes.object.isRequired,
        definition: PropTypes.object.isRequired,
        editable: PropTypes.bool,
        onChange: PropTypes.func,
        onDelete: PropTypes.func,
        isCompact: PropTypes.bool,
        useTEUI: PropTypes.bool,
        hideValidationError: PropTypes.bool,
        checkUniqueObjectField: PropTypes.bool,
        objectId: PropTypes.number,
        onUniqueErrorChange: PropTypes.func,
    };

    static defaultProps = {
        editable: true,
        isCompact: false,
        useTEUI: false,
        checkUniqueObjectField: false,
    };
    static fieldKind: typeof FieldKind;

    state: TFieldInputState = {};

    /** The checkbox whose indeterminate state is set by hand, in the old UI. */
    _checkbox = React.createRef<HTMLInputElement>();
    /** The text input to put the caret in when this is a form's first field. */
    _textFocusField = React.createRef<HTMLInputElement>();
    /** The same, for the new UI's input, whose ref is not an element. */
    _teFocusField = React.createRef<InputRef>();

    _uniqueCheckTimeout = 0;
    _uniqueCheckAbort: AbortController | null = null;
    _uniqueCheckedValue: string | null = null;
    _reportedUniqueError: string | undefined = undefined;

    componentDidMount() {
        if (this._checkbox.current && this.props.indeterminate) {
            this._checkbox.current.indeterminate = true;
        }
        if (this.props.isFirstField) {
            // Put the caret after the value rather than in front of it.
            const focusField = this._textFocusField.current ?? this._teFocusField.current?.input;
            if (focusField) {
                focusField.selectionStart = focusField.selectionEnd = focusField.value.length;
            }
        }
    }

    componentWillUnmount() {
        this.cancelUniqueCheck();
        this.notifyUniqueError(undefined);
    }

    hasValue = () => this.props.field.values && this.props.field.values.length > 0;

    onHoursChange = (event) => {
        this.onLengthChange(event, true);
    };

    onMinutesChange = (event) => {
        this.onLengthChange(event, false);
    };

    onLengthChange = (event, isHours) => {
        if (this.props.editable === false || !this.props.onChange) {
            return;
        }
        let changedValue = parseInt(event.target.value, 10);
        if (isNaN(changedValue)) {
            changedValue = 0;
        }
        let newValue;
        if (isHours) {
            newValue =
                changedValue * TC.SECONDS_PER_HOUR + (this.props.value % TC.SECONDS_PER_HOUR);
        } else {
            newValue =
                changedValue * TC.SECONDS_PER_MINUTE +
                Math.floor(this.props.value / TC.SECONDS_PER_HOUR) * TC.SECONDS_PER_HOUR;
        }

        this.changeIfValid(String(newValue), event);
    };

    onChange = (event) => {
        if (this.props.editable === false || !this.props.onChange) {
            return;
        }

        let newValue = event.target ? event.target.value : event;
        if (this.props.definition.kind === FieldKind.BOOLEAN) {
            newValue = event.target.checked ? "1" : "0";
        }

        this.changeIfValid(newValue, event);
    };

    changeIfValid = (newValue, event) => {
        this.scheduleUniqueCheck(newValue);

        const validation = this._validateInput(newValue);
        if (validation === true) {
            this.reportValidationError(undefined);
            this.props.onChange?.(newValue, event);
            return;
        }

        this.reportValidationError(validation instanceof Error ? validation.message : undefined);

        if (this._validateInput(newValue, { mandatory: false, range: false }) === true) {
            this.props.onChange?.(newValue, event);
            return;
        }
    };

    onBlur = () => {
        this.runUniqueCheck(this.props.value);
    };

    reportValidationError = (message?: string) => {
        if (this.props.useTEUI) {
            this.setState({ validationError: message });
        } else if (message) {
            Log.info(message);
        }
    };

    reportUniqueError = (message?: string) => {
        if (this.props.useTEUI) {
            if (this.state.uniqueError !== message) {
                this.setState({ uniqueError: message });
            }
        } else if (message) {
            Log.info(message);
        }
        this.notifyUniqueError(message);
    };

    notifyUniqueError = (message?: string) => {
        if (this._reportedUniqueError === message) {
            return;
        }
        this._reportedUniqueError = message;
        this.props.onUniqueErrorChange?.(message);
    };

    shouldCheckUnique = () =>
        this.props.checkUniqueObjectField === true &&
        this.props.definition.unique === true &&
        this.props.editable !== false;

    cancelUniqueCheck = () => {
        window.clearTimeout(this._uniqueCheckTimeout);
        this._uniqueCheckTimeout = 0;
        this._uniqueCheckAbort?.abort();
        this._uniqueCheckAbort = null;
    };

    resetUniqueCheck = () => {
        this.cancelUniqueCheck();
        this._uniqueCheckedValue = null;
        this.reportUniqueError(undefined);
    };

    scheduleUniqueCheck = (value) => {
        if (!this.shouldCheckUnique()) {
            return;
        }
        this.resetUniqueCheck();
        if (!value) {
            return;
        }
        this._uniqueCheckTimeout = window.setTimeout(
            () => this.runUniqueCheck(value),
            UNIQUE_CHECK_DEBOUNCE_MS
        );
    };

    runUniqueCheck = (value) => {
        if (!this.shouldCheckUnique() || !value || value === this._uniqueCheckedValue) {
            return;
        }
        if (this._validateInput(value) !== true) {
            return;
        }
        const rootType = TimeEdit.rootType;
        if (rootType === undefined) {
            return;
        }

        this.cancelUniqueCheck();
        this._uniqueCheckedValue = value;
        const abort = new AbortController();
        this._uniqueCheckAbort = abort;

        API.getObjectsWithFieldValues(
            rootType,
            this.props.definition.id,
            [value],
            (objects) => {
                if (abort.signal.aborted) {
                    return;
                }
                this._uniqueCheckAbort = null;
                const isTaken = objects.some(
                    (object) => object.id !== 0 && object.id !== this.props.objectId
                );
                this.reportUniqueError(
                    isTaken
                        ? Language.get(
                              "err_obj_field_unique",
                              this.props.definition.name,
                              value,
                              `${objects[0].id}`
                          )
                        : undefined
                );
            },
            abort.signal
        );
    };

    /**
     * Validate field value. The second parameter is used for limiting which rules the value should be validated against.
     * This is useful to allow invalid states that may lead to a valid state (e.g. if the allowed range is 2-10, the
     * value 1 is invalid, but it should not be prevented since the user may be trying to input the value 10).
     * @param  {string} value Input value
     * @param  {object} rules Object where the rule names are keys and its value a boolean for whether or not the rule should be used for validation
     * @return {boolean|Error} Returns true if the value passed validation, otherwise an error is returned containing a message describing the reason.
     */
    _validateInput = (value, rules: TValidationRules = {}) => {
        if (!value && !this.props.definition.mandatory) {
            return true;
        }
        if (rules.mandatory !== false && !value && this.props.definition.mandatory === true) {
            return new Error(Language.get("err_obj_field_mandatory", this.props.definition.name));
        }

        if (
            rules.length !== false &&
            this.props.definition.length &&
            value.length > this.props.definition.length
        ) {
            return new Error(Language.get("nc_field_error_too_long", this.props.definition.length));
        }

        if (this.props.definition.kind === FieldKind.INTEGER) {
            if (rules.integer !== false && value !== "" && !_.isInteger(value)) {
                return new Error(
                    Language.get("nc_validation_error_not_an_integer", this.props.definition.name)
                );
            }

            const num = parseInt(value, 10);
            const { min, max } = this.props.definition;
            if (
                rules.range !== false &&
                ((min !== undefined && num < min) || (max !== undefined && num > max))
            ) {
                return new Error(
                    Language.get(
                        "err_obj_field_max_min",
                        this.props.definition.name,
                        `${this.props.definition.min || 0} - ${this.props.definition.max}`
                    )
                );
            }
        }

        if (rules.filter !== false && this.props.definition.filter) {
            const regExp = new RegExp(`^${this.props.definition.filter}+$`);
            if (!regExp.test(value)) {
                return new Error(
                    Language.get(
                        "err_obj_field_filter",
                        this.props.definition.name,
                        this.props.definition.filter
                    )
                );
            }
        }

        return true;
    };

    renderControl = () => {
        if (!this.props.definition.multiple || !this.props.editable || !this.props.onDelete) {
            return this._renderInput({ field: true });
        }

        return (
            <div className="">
                <div
                    className={
                        this.props.useTEUI ? "removeButton teUiLeft" : "removeButton leftFloating"
                    }
                    onClick={this.props.onDelete}
                >
                    &#xf068;
                </div>
                {this._renderInput({ field: true })}
            </div>
        );
    };

    render() {
        if (!this.props.useTEUI) {
            return this.renderControl();
        }

        return (
            <>
                {this.renderControl()}
                {this.props.hideValidationError ? null : (
                    // A value that breaks the local rules has a problem the server lookup can't
                    // speak to, so that error goes first.
                    <FieldError message={this.state.validationError ?? this.state.uniqueError} />
                )}
            </>
        );
    }

    _renderInput = (classMap: TFieldClassMap = {}) => {
        const isValid = this._validateInput(this.props.value) === true;
        // eslint-disable-next-line no-param-reassign
        // TODO For TEUI components, replace the validation indication with something better
        classMap = _.extend(
            {
                validField: isValid,
                invalidField: !isValid,
            },
            classMap
        );

        if (this.props.definition.kind === FieldKind.BOOLEAN) {
            if (this.props.useTEUI) {
                return (
                    <Checkbox
                        value={this.props.value}
                        checked={this.hasValue() && this.props.field.values[0] === "1"}
                        indeterminate={this.props.indeterminate}
                        disabled={!this.props.editable}
                        onChange={this.onChange}
                    />
                );
            }
            return (
                <input
                    type="checkbox"
                    value={this.props.value}
                    checked={this.hasValue() && this.props.field.values[0] === "1"}
                    disabled={!this.props.editable}
                    className={_.classSet(classMap)}
                    onChange={this.onChange}
                    ref={this._checkbox}
                />
            );
        }

        if (!this.props.editable && this.props.definition.kind !== FieldKind.CATEGORY) {
            if (this.props.definition.kind === FieldKind.URL) {
                const url = this.props.value;
                const href = prependProtocolToUrl(url);
                return (
                    <div className="fieldLine">
                        <a href={href} target="_blank">
                            {url}
                        </a>
                    </div>
                );
            }
            if (this.props.definition.kind === FieldKind.LENGTH) {
                const val = parseInt(this.props.value, 10);
                let formattedValue = "";
                if (!isNaN(val)) {
                    formattedValue = new MillenniumTime(val).format("HH:mm");
                }
                return <div className="fieldLine">{formattedValue}</div>;
            }
            if (this.props.definition.kind === FieldKind.COMMENT) {
                if (this.props.useTEUI) {
                    return (
                        <TextArea
                            rows={this.props.isCompact ? ROWS_COMPACT : ROWS_LARGE}
                            disabled
                            value={this.props.value}
                        />
                    );
                }
                return (
                    <div className="fieldLine">
                        <textarea
                            rows={this.props.isCompact ? ROWS_COMPACT : ROWS_LARGE}
                            disabled
                            className={_.classSet(classMap)}
                            value={this.props.value}
                        />
                    </div>
                );
            }
            return <div className="fieldLine">{this.props.value}</div>;
        }

        if (this.props.definition.kind === FieldKind.CATEGORY) {
            const categories = (this.props.definition.categories || []).filter(
                (category) =>
                    !this.hasValue() ||
                    category === this.props.value ||
                    this.props.field.values.indexOf(category) === -1
            );
            const inputElement = this.props.useTEUI ? (
                <Select
                    className={_.classSet(classMap)}
                    style={{ width: "100%" }}
                    value={this.props.value}
                    disabled={!this.props.editable}
                    onChange={this.onChange}
                >
                    <option />
                    {categories.map((category, idx) => (
                        <option key={idx} value={category}>
                            {category}
                        </option>
                    ))}
                </Select>
            ) : (
                <select
                    className={_.classSet(classMap)}
                    value={this.props.value}
                    disabled={!this.props.editable}
                    onChange={this.onChange}
                >
                    <option />
                    {categories.map((category, idx) => (
                        <option key={idx} value={category}>
                            {category}
                        </option>
                    ))}
                </select>
            );

            let inputClass = "inputWrapper";
            if (this.props.useTEUI) {
                inputClass = "inputWrapper useTEUI";
            }

            if (this.props.definition.editable && this.props.editable) {
                return (
                    <div className={inputClass}>
                        {inputElement}
                        <input
                            type="text"
                            className="categoryInput"
                            value={this.props.value}
                            onChange={this.onChange}
                            onBlur={this.onBlur}
                        />
                    </div>
                );
            }
            if (this.props.editable) {
                return <div className="fieldLine">{inputElement}</div>;
            }
            return <div className="fieldLine">{this.props.value}</div>;
        }

        const placeholderText = this.props.indeterminate
            ? Language.get("nc_field_multiple_values")
            : "";
        if (this.props.definition.kind === FieldKind.COMMENT) {
            if (this.props.useTEUI) {
                return (
                    <TextArea
                        style={{ fontWeight: "normal" }}
                        rows={this.props.isCompact ? ROWS_COMPACT : ROWS_LARGE}
                        className={_.classSet(classMap)}
                        placeholder={placeholderText}
                        value={this.props.value}
                        onChange={this.onChange}
                        onBlur={this.onBlur}
                    />
                );
            }
            return (
                <textarea
                    rows={this.props.isCompact ? ROWS_COMPACT : ROWS_LARGE}
                    className={_.classSet(classMap)}
                    placeholder={placeholderText}
                    value={this.props.value}
                    onChange={this.onChange}
                    onBlur={this.onBlur}
                />
            );
        }

        // eslint-disable-next-line no-param-reassign
        classMap.nonCategoryInput = true;

        let linkOpener: React.JSX.Element | null = null;
        if (this.props.definition.kind === FieldKind.URL) {
            linkOpener = <a className="linkOpener" href={this.props.value} target="_blank" />;
        }

        if (this.props.definition.kind === FieldKind.LENGTH) {
            // TODO Switch to HourMinute?
            // eslint-disable-next-line no-param-reassign
            classMap.lengthInput = true;
            const time = new MillenniumTime(this.props.value);
            return (
                <div className="inputWrapper">
                    <input
                        type="text"
                        ref={this._textFocusField}
                        autoFocus={this.props.isFirstField}
                        placeholder={placeholderText}
                        value={time.format("H")}
                        className={_.classSet(classMap)}
                        onChange={this.onHoursChange}
                    />
                    :
                    <input
                        type="text"
                        placeholder={placeholderText}
                        value={time.format("mm")}
                        className={_.classSet(classMap)}
                        onChange={this.onMinutesChange}
                    />
                </div>
            );
        }

        if (this.props.useTEUI) {
            // If we need styling, start using a different class name than the old inputWrapper
            // TODO Add validation check and indication?
            return (
                <div>
                    <Input
                        type="text"
                        ref={this._teFocusField}
                        autoFocus={this.props.isFirstField}
                        placeholder={placeholderText}
                        value={this.props.value}
                        onChange={this.onChange}
                        onBlur={this.onBlur}
                    />
                    {linkOpener}
                </div>
            );
        }
        return (
            <div className="inputWrapper">
                <input
                    type="text"
                    ref={this._textFocusField}
                    autoFocus={this.props.isFirstField}
                    placeholder={placeholderText}
                    value={this.props.value}
                    className={_.classSet(classMap)}
                    onChange={this.onChange}
                    onBlur={this.onBlur}
                />
                {linkOpener}
            </div>
        );
    };
}

FieldInput.fieldKind = FieldKind;

export default FieldInput;
